import { useCallback } from 'react';
import intl from 'react-intl-universal';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CENTRALIZED_TASKS_TABLE_TABS } from 'utils/constants/centralizedTasks';
import {
  createDownloadLinkAndDownloadFile,
  extractFilenameFromContentDisposition,
} from 'utils/csvExports';
import { exportTasksList } from 'api/tasksList';
import { useMyTasks } from '../hooks/useMyTasks/useMyTasks';
import useSnackbarNotification from 'Hooks/useSnackbarNotification';
import TasksListTable from 'Pages/TasksListPage/components/v2/TasksListTable/TasksListTable';
import SkeletonLoader from 'Organisms/TableSkeletonLoader/PageTableLoader';

const MyTasks = () => {
  const { normalizedTasksState = false } = useFlags();
  const {
    isLoading,
    hasError,
    myTasks,
    myTasksFilters,
    myTasksSorting,
    canFetchMoreTasks,
    totalCount,
    currentUserId,
    refreshMyTasks,
    handleUpdateFilters,
    handleSearchFilterChange,
    handleTableSort,
    handleFetchMoreTasks,
  } = useMyTasks({ useNormalizedState: normalizedTasksState });

  const {
    isNotificationVisible,
    snackbarProps,
    showNotification,
    SnackbarNotification,
  } = useSnackbarNotification();

  const handleExportTasks = useCallback(async () => {
    try {
      const response = await exportTasksList({
        ...myTasksFilters,
        user: [currentUserId!],
      });
      const filename = extractFilenameFromContentDisposition(
        response.headers['content-disposition']
      );
      const blob = new Blob([response.data], { type: 'text/csv' });
      createDownloadLinkAndDownloadFile(blob, filename);
    } catch (error) {
      showNotification({
        notificationVariant: 'error',
        notificationTitle: intl.get('TASKS_LIST_PAGE.EXPORT_TASKS_ERROR.TITLE'),
        notificationMessage: intl.get(
          'TASKS_LIST_PAGE.EXPORT_TASKS_ERROR.MESSAGE'
        ),
      });
    }
  }, [myTasksFilters, showNotification, currentUserId]);

  if (isLoading) {
    return (
      <SkeletonLoader
        data-testid={`${CENTRALIZED_TASKS_TABLE_TABS.MY_TASKS}__skeleton-loader`}
      />
    );
  }

  return (
    <>
      <TasksListTable
        tableName={CENTRALIZED_TASKS_TABLE_TABS.MY_TASKS}
        tasks={myTasks}
        totalCount={totalCount}
        filters={myTasksFilters}
        onUpdateFilters={handleUpdateFilters}
        sortBy={myTasksSorting.sortBy}
        order={myTasksSorting.order}
        handleSort={handleTableSort}
        canFetchMoreTasks={canFetchMoreTasks}
        fetchMoreTasks={handleFetchMoreTasks}
        onExport={handleExportTasks}
        onSearchFilterChange={handleSearchFilterChange}
        refreshTasks={refreshMyTasks}
        hasError={hasError}
      />
      {isNotificationVisible && snackbarProps && (
        <SnackbarNotification {...snackbarProps} />
      )}
    </>
  );
};

export default MyTasks;
